import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Collapse, Tooltip } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SearchIcon from "@mui/icons-material/Search";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";

import { getSideBarMenuIcon } from "../../common/data";
import { MenuItem, useSideMenu, usePinnedMenu } from "./sidebar/useSideMenu";
import { SEARCH_SHORTCUT } from "./sidebar/CommandPalette";
import "./sidebar/sidebar.css";

export const SIDEBAR_COLLAPSED = "72px";
export const SIDEBAR_EXPANDED = "264px";

const iconFor = (item: MenuItem) => getSideBarMenuIcon(item.path, item.name) ?? <MoreHorizIcon />;
const isInside = (pathname: string, base: string) => pathname === base || pathname.startsWith(`${base}/`);

// White card used for the collapsed rail's tooltips and group fly-outs.
const flyoutProps = {
  tooltip: { className: "sb-flyout" },
  arrow: { className: "sb-flyout-arrow" },
};

interface Props {
  /** Icon-only rail (desktop collapsed). */
  collapsed?: boolean;
  /** Runs after a page is chosen (mobile closes the drawer). */
  onNavigate?: () => void;
  /** Asks the desktop sidebar to expand (clicking a group in the collapsed rail). */
  onExpand?: () => void;
  onSearch: () => void;
}

const PinButton = ({ to, label, pinned, toggle }: { to: string; label: string; pinned: boolean; toggle: (to: string) => void }) => (
  <button
    type="button"
    className={`sb-pin ${pinned ? "on" : ""}`}
    onClick={() => toggle(to)}
    aria-label={pinned ? `Unpin ${label}` : `Pin ${label}`}
    title={pinned ? "Unpin" : "Pin to top"}
  >
    {pinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
  </button>
);

const SideMenu = ({ collapsed = false, onNavigate, onExpand, onSearch }: Props) => {
  const { items, entries, loading } = useSideMenu();
  const { pinned, togglePin } = usePinnedMenu();
  const { pathname } = useLocation();

  // The group containing the current page starts open.
  const currentGroup = items.findIndex((i) => i.inside.length > 0 && isInside(pathname, `/${i.path}`));
  const [open, setOpen] = useState<number | null>(null);
  useEffect(() => {
    if (currentGroup >= 0) setOpen(currentGroup);
  }, [currentGroup]);

  const pinnedEntries = pinned.map((to) => entries.find((e) => e.to === to)).filter(Boolean) as typeof entries;

  if (loading)
    return (
      <div className="sb-nav">
        {Array.from({ length: 9 }, (_, i) => (
          <div key={i} className="sb-skel" style={{ width: collapsed ? 40 : `${70 + ((i * 13) % 25)}%` }} />
        ))}
      </div>
    );

  return (
    <nav className={`sb-nav ${collapsed ? "is-collapsed" : ""}`} aria-label="Main menu">
      {/* Search */}
      <Tooltip title={collapsed ? `Search pages (${SEARCH_SHORTCUT})` : ""} placement="right" arrow componentsProps={flyoutProps}>
        <button type="button" className="sb-search" onClick={onSearch} aria-label="Search pages">
          <SearchIcon />
          <span className="sb-label">Search</span>
          <kbd className="sb-kbd">{SEARCH_SHORTCUT}</kbd>
        </button>
      </Tooltip>

      {/* Pinned */}
      {!collapsed && pinnedEntries.length > 0 && (
        <>
          <div className="sb-section">Pinned</div>
          {pinnedEntries.map((e) => (
            <div className="sb-row" key={`pin-${e.to}`}>
              <NavLink to={e.to} className="sb-item" onClick={onNavigate}>
                <span className="sb-icon">{iconFor(e.item)}</span>
                <span className="sb-label">{e.label}</span>
              </NavLink>
              <PinButton to={e.to} label={e.label} pinned toggle={togglePin} />
            </div>
          ))}
        </>
      )}

      {!collapsed && <div className="sb-section">Menu</div>}

      {items.map((item, index) => {
        const base = `/${item.path}`;
        const icon = iconFor(item);

        /* ── single page ── */
        if (item.inside.length === 0) {
          const link = (
            <NavLink to={base} className="sb-item" onClick={onNavigate} aria-label={collapsed ? item.name : undefined}>
              <span className="sb-icon">{icon}</span>
              <span className="sb-label">{item.name}</span>
            </NavLink>
          );
          return collapsed ? (
            <Tooltip key={item.path} title={item.name} placement="right" arrow componentsProps={flyoutProps}>
              {link}
            </Tooltip>
          ) : (
            <div className="sb-row" key={item.path}>
              {link}
              <PinButton to={base} label={item.name} pinned={pinned.includes(base)} toggle={togglePin} />
            </div>
          );
        }

        /* ── group ── */
        const groupActive = isInside(pathname, base);
        const isOpen = open === index;

        if (collapsed)
          return (
            <Tooltip
              key={item.path}
              placement="right-start"
              arrow
              componentsProps={flyoutProps}
              title={
                <div className="sb-flyout-menu">
                  <div className="sb-flyout-title">{item.name}</div>
                  {item.inside.map((sub) => (
                    <NavLink key={sub.path} to={`${base}/${sub.path}`} className="sb-flyout-link" onClick={onNavigate}>
                      {sub.name}
                    </NavLink>
                  ))}
                </div>
              }
            >
              <button
                type="button"
                className={`sb-item ${groupActive ? "active" : ""}`}
                aria-label={item.name}
                onClick={() => {
                  setOpen(index);
                  onExpand?.();
                }}
              >
                <span className="sb-icon">{icon}</span>
                <span className="sb-label">{item.name}</span>
              </button>
            </Tooltip>
          );

        return (
          <div key={item.path} className={`sb-group ${isOpen ? "open" : ""}`}>
            <button
              type="button"
              className={`sb-item ${groupActive ? "active-parent" : ""}`}
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : index)}
            >
              <span className="sb-icon">{icon}</span>
              <span className="sb-label">{item.name}</span>
              <ExpandMoreRoundedIcon className="sb-chevron" />
            </button>
            <Collapse in={isOpen} timeout={200} unmountOnExit>
              <div className="sb-sub">
                {item.inside.map((sub) => {
                  const to = `${base}/${sub.path}`;
                  return (
                    <div className="sb-row" key={sub.path}>
                      <NavLink to={to} className="sb-sublink" onClick={onNavigate}>
                        <span className="sb-label">{sub.name}</span>
                      </NavLink>
                      <PinButton to={to} label={sub.name} pinned={pinned.includes(to)} toggle={togglePin} />
                    </div>
                  );
                })}
              </div>
            </Collapse>
          </div>
        );
      })}
    </nav>
  );
};

export default SideMenu;
